<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  name: {
    type: String,
    required: false
  }
})

const emit = defineEmits(['updateValue'])

const setValidations = () => {
  if (props.data.required) {
    return 'required'
  }
}

const fullWidthTypes = computed(() => {
  return ['textarea', 'processed_text'].includes(props.data.type)
})

const selectOptions = computed(() => {
  if (!props.data.options) {
    return []
  }

  if (Array.isArray(props.data.options)) {
    return props.data.options
  }

  return Object.entries(props.data.options).map(([value, label]) => ({
    value,
    label
  }))
})

const value = ref('')

watch (value, () => {
  emit('updateValue', props.name, value.value)
})
</script>

<template>
    <div
      class="form-element relative w-full max-w-2/3"
    >
      <FormKit
        v-if="data.type === 'checkbox'"
        validation="required"
        :validation-messages="{
          required: 'Bitte Bestätigen'
        }"
        v-model="value"
        messageClass="text-danger min-h-10 text-xs mt-2"
        decoratorClass="bg-primary"
        wrapperClass="mt-5 flex flex-row items-start lg:items-center"
        inputClass="~w-5/7 ~h-5/7 bg-transparent ~mr-3/4 border-headline"
        :type="data.type"
        :name="name"
      >
        <template #label>
          <span
            class="text-subheadline ~text-xs/base"
            v-html="data.description"
          />
        </template>
      </FormKit>
      <FormKit
        v-else-if="data.type === 'textfield' && data.title != 'E-Mail'"
        type="text"
        v-model="value"
        messageClass="text-primary min-h-10 text-xs mt-2"
        :label="data.title"
        :placeholder="data.required ? `${data.title} *` : data.title"
        :validation="setValidations()"
        placeholderClass="text-headline"
        inputClass="w-full lg:w-2/3 bg-transparent py-3 border-b border-headline"
        labelClass="visually-hidden"
       :name="name"
      />
      <FormKit
        v-else-if="data.type === 'select'"
        type="select"
        v-model="value"
        messageClass="text-danger min-h-10 text-xs mt-2"
        :label="data.title"
        :options="selectOptions"
        :placeholder="data.required ? `${data.title} *` : data.title"
        :validation="setValidations()"
        inputClass="w-full lg:w-2/3 bg-transparent py-3 border-b border-headline"
        labelClass="visually-hidden"
        :name="name"
      />
      <FormKit
        v-else-if="data.type === 'textarea'"
        type="textarea"
        v-model="value"
        messageClass="text-danger min-h-10 text-xs mt-2"
        :label="data.title"
        :placeholder="data.title"
        :validation="setValidations()"
        inputClass="w-full p-2 lg:w-2/3 rounded-md min-h-44 border-black"
        labelClass="visually-hidden"
        :name="name"
      />
      <FormKit
        v-else-if="data.type === 'email' || data.title === 'E-Mail'"
        type="email"
        v-model="value"
        messageClass="text-danger min-h-10 text-xs mt-2"
        :label="data.title"
        :placeholder="data.required ? `${data.title} *` : data.title"
        validation="required|email"
        inputClass="w-full lg:w-2/3 bg-transparent py-3 border-b border-headline"
        placeholderClass="text-headline"
        labelClass="visually-hidden"
        :name="name"
      />
    </div>
</template>

<style lang="scss" scoped>
  .form-element {
    :deep(ul) {
      @apply mb-0;
    }
    :deep(p a) {
      @apply underline text-primary;
    }

    :deep(input), :deep(textarea) {
      &::placeholder {
        @apply text-headline opacity-60;
      }
    }

    :deep(input[type="text"]), :deep(select) {
      &:focus {
        @apply ring-black border-headline;
      }
    }

    :deep(input[type="checkbox"]) {
      &:focus {
        @apply ring-black;
      }
      &:checked {
        @apply bg-primary;
      }
    }
  }
</style>
